import type { WsClientMessage, WsServerMessage } from '@ember/types';

type MessageHandler = (msg: WsServerMessage) => void;
type CloseHandler = () => void;

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

export class RoomWebSocket {
  private ws: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private closeHandlers: CloseHandler[] = [];
  private retryCount = 0;
  private wsUrl = '';
  private shouldReconnect = true;

  connect(wsUrl: string): void {
    this.wsUrl = wsUrl;
    this.shouldReconnect = true;
    this.retryCount = 0;
    this.open();
  }

  private open(): void {
    this.ws = new WebSocket(this.wsUrl);

    this.ws.onopen = () => {
      this.retryCount = 0;
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as WsServerMessage;
        for (const handler of this.messageHandlers) {
          handler(msg);
        }
      } catch {
        console.error('[RoomWebSocket] Failed to parse message:', event.data);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      // Don't reconnect on intentional close codes or if told not to
      if (!this.shouldReconnect || event.code === 1000 || event.code === 1001) {
        for (const handler of this.closeHandlers) {
          handler();
        }
        return;
      }

      if (this.retryCount < MAX_RETRIES) {
        const delay = BASE_RETRY_DELAY_MS * Math.pow(2, this.retryCount);
        this.retryCount++;
        console.warn(
          `[RoomWebSocket] Connection closed, retrying in ${delay}ms (attempt ${this.retryCount}/${MAX_RETRIES})`,
        );
        setTimeout(() => this.open(), delay);
      } else {
        console.error('[RoomWebSocket] Max retries reached, giving up');
        this.shouldReconnect = false;
        for (const handler of this.closeHandlers) {
          handler();
        }
      }
    };

    this.ws.onerror = (event) => {
      console.error('[RoomWebSocket] WebSocket error:', event);
    };
  }

  send(message: WsClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[RoomWebSocket] Cannot send — socket not open');
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close(1000, 'user_disconnect');
      this.ws = null;
    }
  }

  onMessage(handler: MessageHandler): void {
    this.messageHandlers.push(handler);
  }

  onClose(handler: CloseHandler): void {
    this.closeHandlers.push(handler);
  }

  removeAllListeners(): void {
    this.messageHandlers = [];
    this.closeHandlers = [];
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
